import React, { useState, useRef, useEffect } from "react";
import {
  TextField,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  ListItemIcon,
  Box,
  Popper,
  Paper,
  ClickAwayListener,
  InputAdornment,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const MuiNestedDropDown = ({ label, options, onChange, defaultValue = null }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [hoveredOption, setHoveredOption] = useState(null);
  const [selectedLeaves, setSelectedLeaves] = useState({});
  const [selectedElement, setSelectedElement] = useState(null);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [error, setError] = useState("");
  const [pestSubError, setPestSubError] = useState("");
  const closeTimer = useRef(null);

  // Initialize state with defaultValue
  useEffect(() => {
    if (defaultValue) {
      setSelectedElement(defaultValue.element);
      setSelectedLeaves({ [defaultValue.element]: defaultValue.leaves });
    }
  }, [defaultValue]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setError("");
    setPestSubError("");
  };

  const handleCloseAll = () => {
    clearTimeout(closeTimer.current);
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
    setHoveredOption(null);
    setSubmenuOpen(false);

    // Validate main element
    if (!selectedElement) {
      setError("Dette feltet er påkrevd");
    }

    // Validate leaves if a main element is selected
    const leaves = selectedLeaves[selectedElement] || [];
    if (selectedElement && !leaves.length) {
      setPestSubError("Undertype skadegjører er påkrevd");
    }
  };

  const handleLeafToggle = (elementKey, leaf) => {
    setSelectedLeaves((prev) => {
      const current = prev[elementKey] || [];
      const updated = current.includes(leaf)
        ? current.filter((l) => l !== leaf)
        : [...current, leaf];

      let newState = { ...prev };

      if (updated.length > 0) {
        newState[elementKey] = updated;
        setSelectedElement(elementKey);
        setPestSubError("");
      } else {
        delete newState[elementKey];
        // If no leaves are selected for the current element, clear selectedElement
        if (Object.keys(newState).length === 0) {
          setSelectedElement(null);
        }
        setPestSubError("Undertype skadegjører er påkrevd"); // Set error if no leaves
      }

      setError("");
      onChange && onChange({ element: elementKey, leaves: updated });
      return newState;
    });
  };

  const selectedText = selectedElement
    ? selectedLeaves[selectedElement]?.length > 0
      ? `${selectedElement}: ${selectedLeaves[selectedElement].join(", ")}`
      : selectedElement // Display selected element even if no leaves are selected
    : "";

  const handleMouseEnterOption = (option, event) => {
    clearTimeout(closeTimer.current);
    setHoveredOption({
      ...option,
      leaves: [...option.leaves],
    });
    setSubmenuAnchorEl(event.currentTarget);
    setSubmenuOpen(true);
  };

  const handleSubmenuLeave = () => {
    closeTimer.current = setTimeout(() => {
      setSubmenuOpen(false);
      setHoveredOption(null);
    }, 300);
  };

  const handleSubmenuEnter = () => {
    clearTimeout(closeTimer.current);
  };

  return (
    <Box
      sx={{
        width: "100%",
        "& label": {
          color: "#3F4949",
          fontFamily: "Inter",
        },
        "& label.Mui-focused, & label.MuiFormLabel-filled": {
          color: "#00696D",
        },
      }}
    >
      <TextField
        label={label}
        value={selectedText}
        onClick={handleOpen}
        fullWidth
        error={!!error || !!pestSubError}
        helperText={error || pestSubError}
        InputProps={{
          readOnly: true,
          style: { cursor: "pointer" },
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon />
            </InputAdornment>
          ),
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            handleCloseAll();
          }
        }}
        disableAutoFocusItem
        disableEnforceFocus
        PaperProps={{
          style: {
            width: anchorEl?.offsetWidth - 150 || 300,
          },
        }}
        MenuListProps={{
          onMouseLeave: handleSubmenuLeave,
          onMouseEnter: handleSubmenuEnter,
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.element}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={(e) => handleMouseEnterOption(option, e)}
            style={{ width: "100%", height: "45px" }}
          >
            <ListItemText primary={option.element} />
            <ArrowRightIcon fontSize="small" />
          </MenuItem>
        ))}
      </Menu>

      <Popper
        open={submenuOpen && hoveredOption}
        anchorEl={submenuAnchorEl}
        placement="right-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleCloseAll}>
          <Paper
            onMouseEnter={handleSubmenuEnter}
            onMouseLeave={handleSubmenuLeave}
            style={{
              minWidth: 360,
              maxWidth: 400,
              maxHeight: 400,
              overflowY: "auto",
              padding: "4px 0",
              zIndex: 1300,
            }}
          >
            {hoveredOption?.leaves?.map((leaf, index) => (
              <MenuItem
                key={index + "_" + leaf}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLeafToggle(hoveredOption.element, leaf);
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={
                      selectedLeaves[hoveredOption.element]?.includes(leaf) ||
                      false
                    }
                    tabIndex={-1}
                    sx={{
                      color: "#6F7979",
                      "&.Mui-checked": { color: "#00696D" },
                    }}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={leaf} />
              </MenuItem>
            ))}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default MuiNestedDropDown;